import { FC, Suspense, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { DiagnosticsGetStudentsTableQuery } from '../../../../graphQLOperations/__generated__/DiagnosticsGetStudentsTableQuery.graphql';
import { createAndExportCSV } from '../../../../businessLogicService/createAndExportGenericCSV';
import { normalizeProjectionGroup } from '../../../../businessLogicService/normalizeProjectionGroup';
import { DiagnosticsStudentsFetcher } from './ReportDiagnosticStudents';
import ExportToastHost from './ExportToastHost';
import { useExportToasts } from '../../../../businessLogicService/useExportToasts';

interface Props {
  start: number | null;
  roadmapId: string;
  classIds: string[];
  onDone?: () => void;
}

const statusLabel = (completed: boolean, retake?: boolean | null) =>
  retake ? 'Reset' : completed ? 'Completed' : 'Not Completed';

const ExportUnfinishedDiagnosticStudents: FC<Props> = ({ start, roadmapId, classIds, onDone }) => {
  const { downloadToastRef, successToastRef, progress, open, tick, done, clear } = useExportToasts();

  const [queue, setQueue] = useState<string[]>([]);
  const [accum, setAccum] = useState<
    NonNullable<DiagnosticsGetStudentsTableQuery['response']['classDiagnosticReport']>
  >([]);
  const exporting = useMemo(() => queue.length > 0, [queue.length]);

  useEffect(() => {
    if (!start || !roadmapId || classIds.length === 0) return;
    setAccum([]);
    setQueue(classIds);
    open(classIds.length);
  }, [start]);

  const onData = (items: NonNullable<DiagnosticsGetStudentsTableQuery['response']['classDiagnosticReport']>) =>
    setAccum(prev => prev.concat(items ?? []));

  const onDoneClass = () => {
    tick(1);
    setQueue(prev => prev.slice(1));
  };

  useEffect(() => {
    if (exporting) return;
    if (!start) return;
    if (!accum.length) return;

    const totalRoadmapSkills = accum.find(i => typeof i.roadmapSkills === 'number')?.roadmapSkills ?? null;
    const headers = [
      'Name',
      'Status',
      'Date',
      'Demonstrated Skills',
      'Projected Status 2 skills/week',
      'Projected Status 3 skills/week',
    ];

    const acumSorted = accum
      .map(el => el)
      .sort((a, b) =>
        (a.student.lastName ?? '').localeCompare(b.student.lastName ?? '', undefined, { sensitivity: 'base' }),
      );

    const rows = acumSorted.map(item => {
      const dateStr = item.diagnosticDate ? format(new Date(item.diagnosticDate), 'P') : '';
      const demo =
        typeof item.demonstratedSkills === 'number' && typeof totalRoadmapSkills === 'number' && totalRoadmapSkills > 0
          ? `${Math.floor((item.demonstratedSkills * 100) / totalRoadmapSkills)}`
          : '';
      return [
        String(item.student.fullName ?? ''),
        statusLabel(!!item.diagnosticCompleted, item.retakeAssigned),
        dateStr,
        demo,
        String(normalizeProjectionGroup(item.projectedStatusTwoSkills) ?? ''),
        String(normalizeProjectionGroup(item.projectedStatusThreeSkills) ?? ''),
      ];
    });

    try {
      createAndExportCSV(headers, rows);
    } finally {
      done(() => onDone?.());
    }
  }, [exporting]);

  return (
    <>
      <Suspense>
        {exporting && (
          <DiagnosticsStudentsFetcher
            key={`${queue[0]}:${roadmapId}`}
            classId={queue[0]}
            roadmapId={roadmapId}
            onData={onData}
            onDone={onDoneClass}
          />
        )}
      </Suspense>
      <ExportToastHost
        downloadToastRef={downloadToastRef}
        successToastRef={successToastRef}
        progress={progress}
        onClear={clear}
      />
    </>
  );
};

export default ExportUnfinishedDiagnosticStudents;
